import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIIcon } from '../icon/ui-icon';
import type { UIStatusTypes, UIStatusType } from './ui-status.type';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-status.css';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-status
 * @augments {UIElement}
 * @alias UIStatus
 * @element ui-status
 * @classdesc Represents a class for <code>ui-status</code> element.
 * Status is a label with icon. There are only 6 types of statuses, see types.
 * @property {IStatusValue} type {@attr type}
 *  The type of status.
 *  {@desc active}
 *  {@desc inactive}
 *  {@desc pending}
 *  {@desc process}
 *  {@desc domestic-payment}
 *  {@desc international-payment}
 * @property {UIIcon | null} icon Shortcut to icon if exists.
 * @slot
 * @example
 * <ui-status type="active" class="-iconed">
 *   Your service is active.
 * </ui-status>
 */
@Component({ tag: 'ui-status', styles })
export class UIStatus extends UIElement {
    static readonly Types: UIStatusTypes = {
        Active: 'active',
        Inactive: 'inactive',
        Pending: 'pending',
        Process: 'process',
        DomesticPayment: 'domestic-payment',
        InternationalPayment: 'international-payment',
    };

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['type'];
    }

    @Attribute(String) type?: UIStatusType;

    @Child('ui-icon') icon?: UIIcon;

    /**
     * Checks if ui-status has icon inside.
     * @returns {boolean}
     * @private
     */
    private hasIcon(): boolean {
        return this.classList.contains('-iconed');
    }

    /**
     * Builds content.
     * @private
     */
    private buildContent(): void {
        let glyph: NullOr<string> = null;
        switch (this.type) {
            case UIStatus.Types.Active:
                glyph = 'agree';
                break;
            case UIStatus.Types.Inactive:
                glyph = 'minus';
                break;
            case UIStatus.Types.Pending:
                glyph = 'time-hand';
                break;
            case UIStatus.Types.Process:
                glyph = 'options';
                break;
            /* istanbul ignore if */
            default:
                break;
        }
        const newIcon = this.createElement({
            tagName: 'ui-icon',
            attributes: {
                glyph: glyph,
            },
        }) as UIIcon;
        if (glyph && this.hasIcon()) {
            if (this.icon) {
                this.icon.replaceWith(newIcon);
            } else {
                this.insertAdjacentElement('afterbegin', newIcon);
            }
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'type':
                this.buildContent();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.buildContent();
    }
}
